import React from 'react';

/**
 * OwenPresence — Static premium visual presence for Owen Coach
 *
 * Displays Owen's portrait as a constant, stable visual presence.
 * No animation, no transition, no filter change between states.
 * Only status badges change around the image.
 *
 * @param {string} mode - 'idle' | 'listening' | 'speaking' | 'thinking'
 * @param {boolean} hideLabel - Hide the default status label
 */
const OwenPresence = ({ mode = 'idle', hideLabel = false }) => {
  const stateConfig = {
    idle: { label: 'En attente...' },
    listening: { label: "A l'ecoute..." },
    speaking: { label: 'Owen parle...' },
    thinking: { label: '' },
    analyzing: { label: 'Analyse en cours...' },
    completed: { label: 'Session terminée' },
  };

  const config = stateConfig[mode] || stateConfig.idle;

  return (
    <div className="w-full h-full relative" data-testid="owen-presence">
      {/* Owen portrait — constant, no transitions, no filters */}
      <div className="absolute inset-0">
        <img
          src="/images/owen-portrait.jpg"
          alt="Owen - Coach vocal"
          className="w-full h-full object-cover object-top"
          draggable={false}
          data-testid="owen-portrait-image"
        />
        {/* Subtle vignette for cinematic feel — CSS only, always the same */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 50%, rgba(19,10,36,0.6) 100%)',
          }}
        />
        {/* Bottom gradient for badge readability */}
        <div
          className="absolute bottom-0 left-0 right-0 h-28 pointer-events-none"
          style={{
            background: 'linear-gradient(to top, rgba(19,10,36,0.85) 0%, transparent 100%)',
          }}
        />
      </div>

      {/* Status label — floating overlay at bottom */}
      {!hideLabel && config.label && (
        <div className="absolute bottom-4 left-0 right-0 flex justify-center z-20">
          <div
            className={`px-5 py-1.5 rounded-full text-white text-sm font-medium ${
              mode === 'completed' ? 'bg-emerald-700/80' : ''
            }`}
            style={mode !== 'completed' ? {
              background: 'rgba(255, 255, 255, 0.10)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            } : {
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
            data-testid="owen-status-label"
          >
            {config.label}
          </div>
        </div>
      )}

      {/* Audio level indicator — listening mode */}
      {mode === 'listening' && (
        <div className="absolute bottom-14 left-0 right-0 flex justify-center z-20">
          <div className="flex items-end gap-0.5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="w-1 rounded-full bg-blue-400/70 animate-pulse"
                style={{
                  height: `${5 + Math.random() * 10}px`,
                  animationDelay: `${i * 80}ms`,
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OwenPresence;
